import { Component, inject, Input, OnInit } from '@angular/core';

import { ComponentActions, ComponentType, ElementsType } from '../../../state-management/model/enums/state.enums';
import { WebsiteStateService } from '../../../state-management/services/website-state.service';
import { _componentConfig } from './configuration.constants';
import { ComponentItem, WebElement } from '../../../state-management/model/interfaces/state.model';
import { ComponentTemplateService } from '../../services/component-template.service';

import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';

import { v4 as uuidv4 } from 'uuid';
import { firstValueFrom } from 'rxjs';
import { GenericDialog, DialogData } from '../../../components/meta/website-config/website-config-form/website-config-form-page-administration/generic-dialog/generic-dialog.component';
import { ComponentSelectionDialog } from '../component-selection-dialog/component-selection-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-configuration',
  imports: [
    MatIcon,
    MatMenuModule,
    MatButtonModule,
    MatDividerModule,
    MatListModule,
  ],
  templateUrl: './configuration.component.html',
  styleUrl: './configuration.component.scss'
})
export class ConfigurationComponent implements OnInit {
  @Input() cID!: string;
  @Input() sID!: string;
  @Input() cType!: ComponentType;

  public canConfigure!: boolean;
  public canDelete!: boolean;
  public canModifyGroupedElements!: boolean;
  public GroupedElementsLabel!: ElementsType | undefined;
  public LabelDisplay!: string;
  public addLabel!: string;
  public removeLabel!: string;
  public addIcon!: string;
  public removeIcon!: string;
  public hasAction!: {
    label: string;
    action: ComponentActions;
    icon: string;
  } | undefined;


  constructor(
    private dialog: MatDialog,
    private componentTemplateService: ComponentTemplateService
  ) { }


  ngOnInit(): void {
    this.canConfigure = _componentConfig[this.cType]?.canConfigure ?? false;
    this.canDelete = _componentConfig[this.cType]?.canDelete ?? false;
    this.canModifyGroupedElements = _componentConfig[this.cType]?.canModifyGroupedElements ?? false;
    this.GroupedElementsLabel = _componentConfig[this.cType]?.GroupedElementsLabel;
    this.LabelDisplay = _componentConfig[this.cType]?.LabelDisplay ?? 'Element';
    this.addLabel = _componentConfig[this.cType]?.addLabel ?? 'Element hinzufügen';
    this.removeLabel = _componentConfig[this.cType]?.removeLabel ?? 'Element löschen';
    this.addIcon = _componentConfig[this.cType]?.addIcon ?? 'add';
    this.removeIcon = _componentConfig[this.cType]?.removeIcon ?? 'delete';
    this.hasAction = _componentConfig[this.cType]?.hasAction ?? undefined;
    console.log('sID', this.sID, 'cID', this.cID);
  }

  private stateService = inject(WebsiteStateService);

  public triggerAction(action: ComponentActions): void {
    this.stateService.actions = [action];
  }

  public async removeComponent(): Promise<void> {
    const confirmDelete = await this.openDeletePageDialog();
    if (confirmDelete) {
      this.stateService.removeComponent(this.cID, this.sID)
    }

  }

  public removeGroupedElementFromComponent(): void {
    if (!this.GroupedElementsLabel) return;

    let groupedElementIds = this.stateService.getGroupComposedElementIds(this.cID, this.GroupedElementsLabel);
    groupedElementIds()[groupedElementIds().length - 1].map((elementId: string) => {
      this.stateService.removeElement(elementId, this.cID);
    });
  }

  public addGroupedElementToComponent(): void {
    const newBaseCardTemplate = this.createGroupedElementTemplate();
    if (!newBaseCardTemplate) return;

    Object.keys(newBaseCardTemplate).forEach((key) => {
      this.stateService.addElementToComponent(key, newBaseCardTemplate[key], this.cID);
    });
  }

  // Diese Methode wurde durch addComponentAbove() und addComponentBelow() ersetzt


  private openDeletePageDialog(): Promise<boolean> {
    const data: DialogData = {
      title: 'Warnung',
      textChunks: [
        { text: 'Soll diese Komponente endgültig gelöscht werden? Nachdem die Komponente gelöscht wurde, kann sie nicht mehr wiederhergestellt werden' }
      ],
      cancelButtonText: 'Abbrechen',
      confirmButtonText: 'Ja, löschen!',
    };


    const dialogRef = this.dialog.open(GenericDialog, {
      data: data,
      panelClass: "admin-theme"
    });
    return firstValueFrom(dialogRef.afterClosed());
  }



  
  private createGroupedElementTemplate(): Record<string, any> | undefined {
    if (!this.GroupedElementsLabel) return undefined;

    // calculate the max order of the elements in the component with cID
    let elements = this.stateService.getGroupComposedElementIds(this.cID, this.GroupedElementsLabel);
    let maxOrder = 0;
    elements().forEach((elementIds: string[]) => {
      let order = this.stateService.elements()[elementIds[elementIds.length - 1]].order;
      if (order && order > maxOrder) {
        maxOrder = order;
      }
    });
    maxOrder += 1;

    switch (this.GroupedElementsLabel) {
      case ElementsType.BaseCard:
        return this._generateCardTemplate(maxOrder);
      case ElementsType.ComponentImageText:
        return this._generateCompImageTextTemplate(maxOrder);
      case ElementsType.FaqItems:
        return this._generateFAQItemTemplate(maxOrder);
      default:
        return undefined;
    }
  }



  // generate empty composed elements
  private _generateCompImageTextTemplate(maxOrder: number): Record < string, any > {
  const titleUID = uuidv4();
  const descriptionUID = uuidv4();
  const imageUID = uuidv4();

  return {
    [titleUID]: {
      "type": "comp-image-text",
      "value": "Titel des Elements.",
      "tag": "span",
      "id": titleUID,
      "styles": null,
      "label": "title",
      "order": maxOrder
    },
    [descriptionUID]: {
      "type": "comp-image-text",
      "value": "Beschreibung des Elements",
      "tag": "span",
      "id": descriptionUID,
      "styles": null,
      "label": "description",
      "order": maxOrder
    },
    [imageUID]: {
      "type": "comp-image-text",
      "value": null,
      "tag": "img",
      "id": imageUID,
      "styles": null,
      "label": "image",
      "order": maxOrder,
      "options": {
        "altText": "Beschreibung des Bildes"
      }
    }
  }
}
  
  private _generateCardTemplate(maxOrder: number): Record < string, any > {
  const titleUID = uuidv4();
  const descriptionUID = uuidv4();
  const imageUID = uuidv4();

  return {
    [titleUID]: {
      "type": "base-card",
      "value": "Titel",
      "tag": "span",
      "id": titleUID,
      "styles": null,
      "label": "title",
      "order": maxOrder
    },
    [descriptionUID]: {
      "type": "base-card",
      "value": "Beschreibung.",
      "tag": "span",
      "id": descriptionUID,
      "styles": null,
      "label": "description",
      "order": maxOrder
    },
    [imageUID]: {
      "type": "base-card",
      "value": null,
      "tag": "img",
      "id": imageUID,
      "styles": null,
      "label": "image",
      "order": maxOrder,
      "options": {
        "altText": "Beschreibung des Bildes"
      },
      "buttonConfig": {
        "type": "navigate",
        "params": {
          "url": "service/trauer"
        }
      }
    }
  }
}

  private _generateFAQItemTemplate(maxOrder: number): Record < string, any > {
  const questionUID = uuidv4();
  const answerUID = uuidv4();

  return {
    [questionUID]: {
      "type": "faq-items",
      "value": "Beispiel Frage?",
      "tag": "span",
      "id": questionUID,
      "styles": null,
      "label": "question",
      "order": maxOrder
    },
    [answerUID]: {
      "type": "faq-items",
      "value": "Das ist eine Beispiel Antwort.",
      "tag": "span",
      "id": answerUID,
      "styles": null,
      "label": "answer",
      "order": maxOrder
    }
  }
}

  // Neue Komponente oberhalb hinzufügen
  public async addComponentAbove(): Promise<void> {
    const selectedComponentType = await this.openComponentSelectionDialog();
    if (selectedComponentType) {
      const newComponentTemplate = this.createNewComponentTemplate('above', selectedComponentType);
      if (newComponentTemplate) {
        this.addComponentToStateService(newComponentTemplate, 'above');
      }
    }
  }

  // Neue Komponente unterhalb hinzufügen  
  public async addComponentBelow(): Promise<void> {
    const selectedComponentType = await this.openComponentSelectionDialog();
    if (selectedComponentType) {
      const newComponentTemplate = this.createNewComponentTemplate('below', selectedComponentType);
      if (newComponentTemplate) {
        this.addComponentToStateService(newComponentTemplate, 'below');
      }
    }
  }

  // Öffne Dialog zur Komponentenauswahl
  private openComponentSelectionDialog(): Promise<ComponentType | null> {
    const availableComponents = this.componentTemplateService.getAvailableComponents();
    
    const data = {
      title: 'Komponente auswählen',
      textChunks: [
        { text: 'Welche Komponente möchten Sie hinzufügen?' }
      ],
      cancelButtonText: 'Abbrechen',
      customContent: availableComponents
    };

    const dialogRef = this.dialog.open(ComponentSelectionDialog, {
      data: data,
      panelClass: "admin-theme",
      width: '450px'
    });
    
    return firstValueFrom(dialogRef.afterClosed());
  }

  // Template für eine neue Komponente erstellen basierend auf dem gewählten Typ
  private createNewComponentTemplate(position: 'above' | 'below', componentType: ComponentType): { component: ComponentItem, elements: WebElement[] } | null {
    const currentSectionComponents = this.getCurrentSectionComponents();
    const currentComponentIndex = currentSectionComponents.findIndex(comp => comp.id === this.cID);
    
    if (currentComponentIndex === -1) {
      console.error('Aktuelle Komponente nicht in Section gefunden');
      return null;
    }

    // Berechne die neue Order basierend auf der Position
    let newOrder: number;
    if (position === 'above') {
      if (currentComponentIndex === 0) {
        newOrder = currentSectionComponents[0].order - 1;
      } else {
        const prevComponent = currentSectionComponents[currentComponentIndex - 1];
        const currentComponent = currentSectionComponents[currentComponentIndex];
        newOrder = (prevComponent.order + currentComponent.order) / 2;
      }
    } else { // below
      if (currentComponentIndex === currentSectionComponents.length - 1) {
        newOrder = currentSectionComponents[currentComponentIndex].order + 1;
      } else {
        const currentComponent = currentSectionComponents[currentComponentIndex];
        const nextComponent = currentSectionComponents[currentComponentIndex + 1];
        newOrder = (currentComponent.order + nextComponent.order) / 2;
      }
    }

    // Verwende ComponentTemplateService mit dem gewählten Komponententyp
    const componentTemplate = this.componentTemplateService.createComponentByType(componentType);
    
    if (!componentTemplate) {
      console.error(`Komponententyp ${componentType} wird nicht unterstützt`);
      return null;
    }
    
    // Aktualisiere die Order der Komponente
    const component: ComponentItem = {
      ...componentTemplate.component,
      order: newOrder
    };

    return { component, elements: componentTemplate.elements };
  }

  // Diese Methode wurde durch ComponentTemplateService.createFaqComponent() ersetzt

  // Hole die aktuellen Komponenten der Section
  private getCurrentSectionComponents(): any[] {
    const sortedActiveComponents = this.stateService.sortedActiveComponents();
    return sortedActiveComponents[this.sID] || [];
  }

  // Füge die neue Komponente zum StateService hinzu
  private addComponentToStateService(template: { component: ComponentItem, elements: WebElement[] }, position: 'above' | 'below'): void {
    // Füge die Komponente hinzu
    this.stateService.addComponent(template.component.id, template.component);
    
    // Füge alle Elemente hinzu
    template.elements.forEach(element => {
      this.stateService.addElementToComponent(element.id, element, template.component.id);
    });

    // Aktualisiere die Section mit der neuen Komponente
    const currentSectionComponents = this.getCurrentSectionComponents();
    const currentComponentIndex = currentSectionComponents.findIndex(comp => comp.id === this.cID);
    
    const updatedComponents = [...currentSectionComponents.map(comp => comp.id)];
    
    if (position === 'above') {
      updatedComponents.splice(currentComponentIndex, 0, template.component.id);
    } else { // below
      updatedComponents.splice(currentComponentIndex + 1, 0, template.component.id);
    }

    // Aktualisiere die Section
    this.stateService.updateSection(this.sID, { components: updatedComponents });
  }

}
